import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Bell,
  Activity,
  FileText,
  Settings,
  AlertTriangle,
  Clock,
  CheckCheck,
  Filter,
  ExternalLink,
} from 'lucide-react';
import { useNotificationsFeed } from '@/hooks/useNotificationsFeed';
import { formatDistanceToNow } from 'date-fns';
import { announceToScreenReader } from '@/lib/a11y';
import { LoadingState } from '@/components/ui/loading-state';
import { EmptyState } from '@/components/ui/empty-state';

interface NotificationsCenterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const iconMap = {
  activity: Activity,
  report: FileText,
  system: Settings,
  error: AlertTriangle,
};

const colorMap = {
  activity: 'text-green-600 bg-green-50',
  report: 'text-yellow-600 bg-yellow-50',
  system: 'text-blue-600 bg-blue-50',
  error: 'text-red-600 bg-red-50',
};

export function NotificationsCenter({ open, onOpenChange }: NotificationsCenterProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [timeFilter, setTimeFilter] = useState('7d');
  
  const {
    data: notifications = [],
    isLoading,
    unreadCount,
    readIds,
    markAsRead,
    markAllAsRead,
  } = useNotificationsFeed();

  // Helper function to filter notifications by tab and time
  const getFilteredNotifications = (tabValue: string) => {
    return notifications.filter((notification) => {
      // Tab filter
      const tabMatch = tabValue === 'all' || notification.type === tabValue;
      
      // Time filter
      const now = new Date();
      const notificationDate = new Date(notification.created_at);
      let timeMatch = true;
      
      if (timeFilter === '24h') {
        const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        timeMatch = notificationDate >= last24h;
      } else if (timeFilter === '7d') {
        const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        timeMatch = notificationDate >= last7d;
      }
      
      return tabMatch && timeMatch;
    });
  };

  // Announce new notifications
  useEffect(() => {
    const unreadNotifications = notifications.filter(n => !readIds.has(n.id));
    if (unreadNotifications.length > 0) {
      const latestNotification = unreadNotifications[0];
      announceToScreenReader(`New ${latestNotification?.type} notification: ${latestNotification?.message}`);
    }
  }, [notifications, readIds]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    
    if (notification.href) {
      navigate(notification.href);
      onOpenChange(false);
    }
  };

  const handleMarkAllAsRead = () => {
    const currentFiltered = getFilteredNotifications(activeTab);
    const allIds = currentFiltered.map(n => n.id);
    markAllAsRead(allIds);
  };

  const getTypeCount = (type: string) => {
    return notifications.filter(n => n.type === type).length;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[32rem] overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications Center
              {unreadCount > 0 && (
                <Badge variant="destructive" className="animate-pulse">
                  {unreadCount}
                </Badge>
              )}
            </SheetTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={getFilteredNotifications(activeTab).filter(n => !readIds.has(n.id)).length === 0}
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {/* Controls */}
          <div className="flex items-center gap-2">
            <Select value={timeFilter} onValueChange={setTimeFilter}>
              <SelectTrigger className="w-32">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 hours</SelectItem>
                <SelectItem value="7d">7 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5 gap-2">
              <TabsTrigger value="all">
                All
                <Badge variant="secondary" className="px-2 text-xs">
                  {notifications.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="activity">
                Activity
                <Badge variant="secondary" className="px-2 text-xs">
                  {getTypeCount('activity')}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="report">
                Reports
                <Badge variant="secondary" className="px-2 text-xs">
                  {getTypeCount('report')}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="system">
                System
                <Badge variant="secondary" className="px-2 text-xs">
                  {getTypeCount('system')}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="error">
                Errors
                <Badge variant="destructive" className="px-2 text-xs">
                  {getTypeCount('error')}
                </Badge>
              </TabsTrigger>
            </TabsList>

            {(['all', 'activity', 'report', 'system', 'error'] as const).map((tabValue) => (
              <TabsContent key={tabValue} value={tabValue} className="mt-4">
                {(() => {
                  const tabFilteredNotifications = getFilteredNotifications(tabValue);
                  return isLoading ? (
                    <LoadingState variant="skeleton" skeletonCount={5} message="Loading notifications..." />
                  ) : tabFilteredNotifications.length > 0 ? (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                      {tabFilteredNotifications.map((notification) => {
                      const IconComponent = iconMap[notification.type];
                      const isRead = readIds.has(notification.id);
                      
                      return (
                        <div
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-muted/50 ${
                            !isRead ? 'bg-primary/5 border-l-4 border-l-primary' : 'bg-muted/20'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 p-2 rounded-full ${colorMap[notification.type]}`}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              {notification.title && (
                                <p className={`text-sm font-medium ${!isRead ? 'font-semibold' : ''}`}>
                                  {notification.title}
                                </p>
                              )}
                              <p className={`text-sm ${notification.title ? 'text-muted-foreground mt-1' : notification.message ? 'font-medium' : ''} ${!isRead && !notification.title ? 'font-semibold' : ''}`}>
                                {notification.message}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Clock className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                </span>
                                {notification.user_email && (
                                  <span className="text-xs text-muted-foreground">
                                    • {notification.user_email}
                                  </span>
                                )}
                                {notification.href && (
                                  <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto" />
                                )}
                              </div>
                            </div>
                            {!isRead && (
                              <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-2" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  ) : (
                    <EmptyState
                      icon={Bell}
                      title={
                        tabValue === 'all' 
                          ? 'No notifications' 
                          : `No ${tabValue} notifications`
                      }
                      description={
                        tabValue === 'all' 
                          ? 'No notifications in the selected time period.' 
                          : `No ${tabValue} notifications in the selected time period.`
                      }
                      variant="no-data"
                    />
                  );
                })()}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
